<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import mercadolibreClient from 'dashboard/api/channel/mercadolibreClient';
import Button from 'dashboard/components-next/button/Button.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import Input from 'dashboard/components-next/input/Input.vue';

const { t } = useI18n();

const SITE_OPTIONS = [
  { value: 'MLA', label: 'Argentina (MLA)' },
  { value: 'MLB', label: 'Brasil (MLB)' },
  { value: 'MLM', label: 'México (MLM)' },
  { value: 'MLC', label: 'Chile (MLC)' },
  { value: 'MCO', label: 'Colombia (MCO)' },
  { value: 'MLU', label: 'Uruguay (MLU)' },
  { value: 'MPE', label: 'Perú (MPE)' },
  { value: 'MEC', label: 'Ecuador (MEC)' },
  { value: 'MBO', label: 'Bolivia (MBO)' },
  { value: 'MLV', label: 'Venezuela (MLV)' },
  { value: 'MCR', label: 'Costa Rica (MCR)' },
  { value: 'MRD', label: 'República Dominicana (MRD)' },
  { value: 'MPA', label: 'Panamá (MPA)' },
];

const hasError = ref(false);
const errorStateMessage = ref('');
const errorStateDescription = ref('');
const isRequestingAuthorization = ref(false);
const selectedSite = ref('MLA');
const appId = ref('');
const appSecret = ref('');
const secretConfigured = ref(false);
const redirectUri = ref('');
const callbackUrl = ref('');

const canContinue = computed(
  () =>
    Boolean(selectedSite.value) &&
    Boolean(appId.value) &&
    (Boolean(appSecret.value) || secretConfigured.value) &&
    Boolean(redirectUri.value) &&
    Boolean(callbackUrl.value)
);

const showLimitError = (limit = 1) => {
  hasError.value = true;
  errorStateMessage.value = t('INBOX_MGMT.ADD.MERCADOLIBRE.ERROR_LIMIT', {
    n: Number(limit) || 1,
  });
  errorStateDescription.value = '';
};

const applyRequestError = error => {
  const data = error?.response?.data || {};
  if (
    data.error_code === 'inbox_limit_exceeded' ||
    String(data.error || '').includes('inbox limit reached')
  ) {
    showLimitError(data.limit);
    return;
  }

  hasError.value = true;
  errorStateMessage.value =
    data.error || t('INBOX_MGMT.ADD.MERCADOLIBRE.ERROR_MESSAGE');
  errorStateDescription.value = t('INBOX_MGMT.ADD.MERCADOLIBRE.ERROR_AUTH');
};

const loadConfiguration = async () => {
  try {
    const { data } = await mercadolibreClient.getConfiguration();
    if (data.app_id) {
      appId.value = data.app_id;
    }
    secretConfigured.value = Boolean(data.app_secret_configured);
    redirectUri.value = data.redirect_uri || '';
    callbackUrl.value = data.callback_url || '';
  } catch (error) {
    applyRequestError(error);
  }
};

const copyUrl = async url => {
  if (!url) {
    return;
  }

  await copyTextToClipboard(url);
  useAlert(t('INBOX_MGMT.ADD.MERCADOLIBRE.COPIED'));
};

onMounted(() => {
  const urlParams = new URLSearchParams(window.location.search);
  const errorCode = urlParams.get('code');
  const errorMessage = urlParams.get('error_message');
  const errorCodeName = urlParams.get('error_code');
  const errorType = urlParams.get('error_type');

  if (
    errorCodeName === 'inbox_limit_exceeded' ||
    String(errorType || '').includes('InboxLimitExceeded') ||
    String(errorMessage || '').includes('inbox limit reached')
  ) {
    showLimitError(urlParams.get('limit') || 1);
  } else if (errorMessage) {
    hasError.value = true;
    if (errorCode === '400' || errorCode === '409') {
      errorStateMessage.value = errorMessage;
      errorStateDescription.value = t('INBOX_MGMT.ADD.MERCADOLIBRE.ERROR_AUTH');
    } else {
      errorStateMessage.value = t('INBOX_MGMT.ADD.MERCADOLIBRE.ERROR_MESSAGE');
      errorStateDescription.value = errorMessage;
    }
  }

  const cleanURL = window.location.pathname;
  window.history.replaceState({}, document.title, cleanURL);
  if (!hasError.value) {
    loadConfiguration();
  }
});

const requestAuthorization = async () => {
  isRequestingAuthorization.value = true;
  try {
    await mercadolibreClient.updateConfiguration({
      app_id: appId.value,
      site_id: selectedSite.value,
      ...(appSecret.value ? { app_secret: appSecret.value } : {}),
    });
    if (appSecret.value) {
      secretConfigured.value = true;
    }

    const response = await mercadolibreClient.generateAuthorization({
      site_id: selectedSite.value,
    });
    const {
      data: { url },
    } = response;

    window.location.href = url;
  } catch (error) {
    applyRequestError(error);
    isRequestingAuthorization.value = false;
  }
};
</script>

<template>
  <div class="h-full p-6 w-full max-w-full flex-shrink-0 flex-grow-0">
    <div class="flex flex-col items-center justify-start h-full text-center">
      <div v-if="hasError" class="max-w-lg mx-auto text-center">
        <h5>{{ errorStateMessage }}</h5>
        <p
          v-if="errorStateDescription"
          v-dompurify-html="errorStateDescription"
        />
      </div>
      <div
        v-else
        class="flex flex-col items-center justify-center px-8 py-10 text-center rounded-2xl outline outline-1 outline-n-weak max-w-lg"
      >
        <h6 class="text-2xl font-medium">
          {{ $t('INBOX_MGMT.ADD.MERCADOLIBRE.CONNECT_YOUR_ACCOUNT') }}
        </h6>
        <p class="py-6 text-sm text-n-slate-11">
          {{ $t('INBOX_MGMT.ADD.MERCADOLIBRE.HELP') }}
          <a
            href="https://developers.mercadolibre.com.ar/devcenter"
            target="_blank"
            rel="noopener noreferrer"
            class="text-n-brand"
          >
            {{ $t('INBOX_MGMT.ADD.MERCADOLIBRE.DEVELOPERS_LINK') }}
          </a>
        </p>
        <div class="flex flex-col w-full gap-4 mb-6 text-left">
          <div>
            <p class="mb-1 text-sm text-n-slate-12">
              {{ $t('INBOX_MGMT.ADD.MERCADOLIBRE.CALLBACK') }}
            </p>
            <div class="flex items-center gap-2">
              <code
                class="flex-1 min-w-0 truncate text-xs text-n-slate-11 bg-n-alpha-2 rounded-lg px-3 py-2"
              >
                {{ callbackUrl }}
              </code>
              <Button
                ghost
                sm
                :label="$t('INBOX_MGMT.ADD.MERCADOLIBRE.COPY')"
                @click="copyUrl(callbackUrl)"
              />
            </div>
          </div>
          <div>
            <p class="mb-1 text-sm text-n-slate-12">
              {{ $t('INBOX_MGMT.ADD.MERCADOLIBRE.REDIRECT_URI') }}
            </p>
            <div class="flex items-center gap-2">
              <code
                class="flex-1 min-w-0 truncate text-xs text-n-slate-11 bg-n-alpha-2 rounded-lg px-3 py-2"
              >
                {{ redirectUri }}
              </code>
              <Button
                ghost
                sm
                :label="$t('INBOX_MGMT.ADD.MERCADOLIBRE.COPY')"
                @click="copyUrl(redirectUri)"
              />
            </div>
          </div>
          <p class="text-sm text-n-slate-11">
            {{ $t('INBOX_MGMT.ADD.MERCADOLIBRE.OAUTH') }}
          </p>
          <p class="text-sm text-n-slate-11">
            {{ $t('INBOX_MGMT.ADD.MERCADOLIBRE.PERMISSIONS') }}
          </p>
          <p class="text-sm text-n-slate-11">
            {{ $t('INBOX_MGMT.ADD.MERCADOLIBRE.TOPICS') }}
          </p>
          <Input
            v-model="appId"
            :label="$t('INBOX_MGMT.ADD.MERCADOLIBRE.APP_ID')"
            :placeholder="$t('INBOX_MGMT.ADD.MERCADOLIBRE.APP_ID_PLACEHOLDER')"
          />
          <Input
            v-model="appSecret"
            type="password"
            :label="$t('INBOX_MGMT.ADD.MERCADOLIBRE.APP_SECRET')"
            :placeholder="
              $t('INBOX_MGMT.ADD.MERCADOLIBRE.APP_SECRET_PLACEHOLDER')
            "
          />
          <div>
            <p class="mb-2 text-sm text-n-slate-12">
              {{ $t('INBOX_MGMT.ADD.MERCADOLIBRE.SELECT_COUNTRY') }}
            </p>
            <ComboBox
              v-model="selectedSite"
              :options="SITE_OPTIONS"
              :placeholder="$t('INBOX_MGMT.ADD.MERCADOLIBRE.SELECT_COUNTRY')"
            />
          </div>
        </div>
        <Button
          lg
          icon="i-ri-shopping-bag-3-line"
          :disabled="isRequestingAuthorization || !canContinue"
          :is-loading="isRequestingAuthorization"
          :label="$t('INBOX_MGMT.ADD.MERCADOLIBRE.CONTINUE')"
          @click="requestAuthorization()"
        />
      </div>
    </div>
  </div>
</template>
